import { type ReactElement } from 'react';
import {
  AuthState,
  useIdentify,
  type CustomerAuthState,
} from '../../hooks/useIdentify';

// Define local auth state types to avoid circular dependencies
type AuthStateAuthenticated = CustomerAuthState & {
  authState: typeof AuthState.isAuthenticated;
};
type AuthStateAnonymous = CustomerAuthState & {
  authState: typeof AuthState.isAnonymous;
};
type AuthStateLoading = CustomerAuthState & {
  authState: typeof AuthState.isLoading;
};
type AuthStateError = CustomerAuthState & {
  authState: typeof AuthState.isError;
};

interface AuthGuardProps {
  authenticated?: (state: AuthStateAuthenticated) => ReactElement;
  anonymous?: (state: AuthStateAnonymous) => ReactElement;
  loading?: (state: AuthStateLoading) => ReactElement;
  error?: (state: AuthStateError) => ReactElement;
}

export function AuthGuard(props: AuthGuardProps) {
  const { authenticated, anonymous, loading, error } = props;
  const auth = useIdentify();

  if (authenticated && auth.authState === AuthState.isAuthenticated) {
    return authenticated(auth);
  }
  if (anonymous && auth.authState === AuthState.isAnonymous) {
    return anonymous(auth);
  }
  if (loading && auth.authState === AuthState.isLoading) {
    return loading(auth);
  }
  if (error && auth.authState === AuthState.isError) {
    return error(auth);
  }
  return null;
}
